import { putCommunityReport } from '@/src/apis/rest/communityReport';
import { PutCommunityReportById } from '@/src/apis/rest/communityReport.dtos';
import Button from '@/src/components/atom/Button';
import Dialog from '@/src/components/atom/Dialog';
import Select from '@/src/components/atom/Select';
import TextArea from '@/src/components/atom/TextArea';
import FormContent from '@/src/components/molecule/FormContent';
import { Form, FormField } from '@/src/components/ui/form';
import { BUTTON_CONST } from '@/src/constants/buttons';
import { TOAST_CONST } from '@/src/constants/toast';
import useDialog, { DialogType } from '@/src/hooks/useDialog';
import { toast } from '@/src/hooks/useToast';
import CommunityReportDto from '@/src/models/CommunityReportDto';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const formSchema = () => {
  return z.object({
    communityBlindStatusType: z.string().min(1),
    reportHandlingReason: z.string().min(1),
  });
};

type FormType = Partial<PutCommunityReportById> & {
  communityBlindStatusType: PutCommunityReportById['communityReportType'];
};

interface CommunityReportBlindModalProps {
  open: boolean;
  onOpenChange: (value: boolean) => void;
  onSubmit: () => void;
  data?: CommunityReportDto | null;
}

export default function CommunityReportBlindModal({
  open: openProps,
  onOpenChange: onOpenChangeProps,
  onSubmit,
  data,
}: CommunityReportBlindModalProps) {
  // util
  const {
    openDialog,
    closeDialog,
    isOpen,
    Dialog: ApplyAlert,
  } = useDialog({
    type: DialogType.ALERT,
  });

  // mutate
  const putCommunityReportMutate = useMutation({
    mutationFn: putCommunityReport,
  });

  // form
  const form = useForm<FormType>({
    resolver: zodResolver(formSchema()),
    defaultValues: {
      id: data?.id,
      reportHandlingReason: '',
    },
  });

  // event
  const handleSubmit = (values: FormType) => {
    const newValues = {
      ...(values as PutCommunityReportById),
      communityBlindStatusType: undefined,
      communityReportType: values.communityBlindStatusType,
    };

    openDialog();
    putCommunityReportMutate.mutate(newValues, {
      onSuccess: () => {
        toast({
          variant: 'success',
          title: TOAST_CONST.saveSuccess.title,
          description: TOAST_CONST.saveSuccess.description,
        });

        onSubmit();

        handleCloseModal();
      },
      onError: e => {
        toast({
          variant: 'error',
          title: TOAST_CONST.error.title,
          description: e.message,
        });
      },
    });
  };
  const handleSubmitClick = () => {
    openDialog();
  };
  const handleCloseModal = () => {
    onOpenChangeProps(false);
  };

  // render
  const renderApplyAlertText = () => {
    switch (form.watch('communityBlindStatusType')) {
      case 'blind':
        return {
          title: '블라인드 처리 확인',
          description: (
            <>
              해당 게시글을 블라인드 처리를 하시겠습니까?
              <br />
              블라인드 처리 이후 게시는 불가능합니다.
            </>
          ),
        };
      case 'common':
        return {
          title: '신고 반려 처리 확인',
          description: (
            <>
              해당 게시글을 신고 반려 처리를 하시겠습니까?
              <br />
              신고 반려 처리 이후 블라인드 처리는 불가능합니다.
            </>
          ),
        };
      default:
        return {
          title: '블라인드 처리 확인',
          description: (
            <>
              해당 게시글을 블라인드 처리를 하시겠습니까?
              <br />
              블라인드 처리 이후 게시는 불가능합니다.
            </>
          ),
        };
    }
  };

  const renderFooter = () => {
    return (
      <div className="flex justify-end gap-2">
        <Button
          className="flex-1"
          buttonColor="outline"
          onClick={handleCloseModal}
        >
          {BUTTON_CONST.cancel}
        </Button>
        {data?.reportHandledAt ? (
          <Button
            type="button"
            className="flex-1"
            onClick={() => handleCloseModal()}
          >
            {BUTTON_CONST.ok}
          </Button>
        ) : (
          <Button
            type="submit"
            className="flex-1"
            onClick={() => handleSubmitClick()}
            disabled={!form.formState.isValid}
            isLoading={putCommunityReportMutate.isPending}
          >
            {BUTTON_CONST.apply}
          </Button>
        )}
      </div>
    );
  };

  return (
    <>
      <Dialog
        open={openProps}
        onOpenChange={handleCloseModal}
        contentProps={{
          style: {
            width: 'calc(100vw - 24px)',
          },
          className: 'max-w-[697px]',
        }}
        title="블라인드 처리"
        footer={renderFooter()}
      >
        <Form {...form}>
          <form
            className="w-full"
            onSubmit={form.handleSubmit(handleSubmitClick)}
          >
            <button className="hidden" type="submit" />
            <div className="flex flex-col gap-6 [&:last-child]:flex-1">
              <FormField
                control={form.control}
                name="communityBlindStatusType"
                render={({ field, fieldState: { error } }) => {
                  return (
                    <FormContent
                      className="w-full flex-col gap-2 [&>div:last-child]:w-full [&>label]:translate-y-0"
                      label="블라인드 여부"
                      isRequired
                      error={error}
                    >
                      <Select
                        option={[
                          {
                            optionList: [
                              { label: '블라인드 처리', value: 'blind' },
                              { label: '신고 반려', value: 'common' },
                            ],
                          },
                        ]}
                        rootProps={{
                          value: data?.reportHandledAt
                            ? data?.communityReportType
                            : field.value,
                          onValueChange: field.onChange,
                        }}
                        triggerProps={{
                          className: 'w-full',
                          disabled: !!data?.reportHandledAt,
                        }}
                        valueProps={{
                          placeholder: '선택',
                        }}
                        {...field}
                      />
                    </FormContent>
                  );
                }}
              />
              <FormField
                control={form.control}
                name="reportHandlingReason"
                render={({ field, fieldState: { error } }) => {
                  return (
                    <FormContent
                      className="w-full flex-1 flex-col gap-2 [&>div:last-child]:w-full [&>label]:translate-y-0"
                      label="사유 입력"
                      isRequired
                      error={error}
                    >
                      <TextArea
                        {...field}
                        disabled={!!data?.reportHandledAt}
                        className="h-auto min-h-[220px] w-full flex-1"
                        placeholder="블라인드 또는 게시 사유를 입력해주세요"
                      />
                    </FormContent>
                  );
                }}
              />
            </div>
          </form>
        </Form>
      </Dialog>
      {isOpen && (
        <ApplyAlert
          open={isOpen}
          onOpenChange={closeDialog}
          onOk={() => handleSubmit(form.watch())}
          title={renderApplyAlertText().title}
          okButtonProps={{
            isLoading: putCommunityReportMutate.isPending,
          }}
        >
          {renderApplyAlertText().description}
        </ApplyAlert>
      )}
    </>
  );
}
